(() => {
  if (window.GMLMobileEditorial) {
    window.GMLMobileEditorial.init();
    return;
  }

  const mobile = window.matchMedia('(max-width: 749px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();
  const clamp = (value) => Math.max(0, Math.min(1, value));
  const smooth = (value) => value * value * (3 - 2 * value);
  const rootSelector = '[data-gml-story], .gml-interests, .gml-about, .gml-editorial';
  const paragraphSelector = '.gml-story__body p, .gml-interests__prose p, .gml-interests__pick, .gml-about__body p, .gml-editorial__body p, .gml-editorial__chapter-copy p, .gml-editorial__contact-copy > p';
  const cardSelector = '.gml-story__chapter-media, .gml-story__detail, .gml-interests__figure, .gml-about__figure, .gml-editorial__chapter-media';
  const chapterSelector = '.gml-story__chapter, .gml-interests__chapter, .gml-editorial__chapter';
  let frame = 0;

  const splitParagraph = (element) => {
    const replacements = [];
    const words = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => node.parentElement.closest('svg, script, style, [aria-hidden="true"]') || !node.textContent.trim()
        ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const locale = document.documentElement.lang || 'en';
    let segmenter;
    try { segmenter = new Intl.Segmenter(locale, { granularity: 'word' }); } catch (_) {}
    nodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      const tokens = segmenter
        ? Array.from(segmenter.segment(node.textContent), (part) => part.segment)
        : node.textContent.split(/(\s+)/);
      const added = [];
      tokens.forEach((token) => {
        const part = /\S/.test(token) ? document.createElement('span') : document.createTextNode(token);
        if (part.nodeType === Node.ELEMENT_NODE) {
          part.className = 'gml-mobile-word';
          part.textContent = token;
          words.push(part);
        }
        added.push(part);
        fragment.appendChild(part);
      });
      node.replaceWith(fragment);
      replacements.push({ node, added });
    });
    element.setAttribute('data-gml-ink', '');
    return { element, words, progress: -1, replacements };
  };

  const makeGroups = (root, cards) => {
    const groups = [];
    const cardElements = new Set(cards.map((card) => card.element));
    const containers = root.querySelectorAll('.gml-story__editorial, .gml-interests__chapters, .gml-editorial__chapters');
    containers.forEach((container) => {
      let run = [];
      const finish = () => {
        if (run.length > 1) {
          const wrapper = document.createElement('div');
          wrapper.className = 'gml-mobile-stack';
          run[0].before(wrapper);
          const sections = run.map((element, index) => {
            wrapper.appendChild(element);
            element.classList.add('gml-mobile-stack-section');
            element.style.setProperty('--gml-stack-layer', String(index + 1));
            const card = cards.find((item) => element.contains(item.element));
            card.group = wrapper;
            card.section = element;
            return { element, card };
          });
          groups.push({ wrapper, sections });
        }
        run = [];
      };
      Array.from(container.children).forEach((element) => {
        const hasCard = element.matches(chapterSelector) && Array.from(element.querySelectorAll(cardSelector)).some((card) => cardElements.has(card));
        if (hasCard) run.push(element);
        else finish();
      });
      finish();
    });
    return groups;
  };

  const enhance = (root) => {
    if (controllers.has(root)) return;
    root.classList.add('gml-mobile-editorial-root');
    const state = { root, paragraphs: [], cards: [], groups: [] };
    controllers.set(root, state);
    root.querySelectorAll(cardSelector).forEach((element) => {
      const image = element.querySelector('img');
      if (!image || getComputedStyle(element).display === 'none') return;
      element.classList.add('gml-mobile-image-card');
      state.cards.push({ element, image });
    });
    if (reduced.matches) return;
    state.paragraphs = Array.from(root.querySelectorAll(paragraphSelector)).filter((element) => element.textContent.trim()).map(splitParagraph);
    state.groups = makeGroups(root, state.cards);
    state.resize = new ResizeObserver(schedule);
    state.resize.observe(root);
    state.cards.forEach((card) => state.resize.observe(card.element));
    update();
  };

  const restore = (state) => {
    state.resize?.disconnect();
    state.paragraphs.forEach(({ element, replacements }) => {
      replacements.forEach(({ node, added }) => {
        if (!added[0]?.parentNode) return;
        added[0].before(node);
        added.forEach((part) => part.remove());
      });
      element.removeAttribute('data-gml-ink');
    });
    state.cards.forEach(({ element }) => {
      element.classList.remove('gml-mobile-image-card');
      ['--gml-card-rise', '--gml-card-opacity'].forEach((property) => element.style.removeProperty(property));
    });
    state.groups.forEach(({ wrapper, sections }) => {
      sections.forEach(({ element }) => {
        element.classList.remove('gml-mobile-stack-section');
        element.style.removeProperty('--gml-stack-top');
        element.style.removeProperty('--gml-stack-layer');
        wrapper.before(element);
      });
      wrapper.remove();
    });
    state.root.classList.remove('gml-mobile-editorial-root');
    controllers.delete(state.root);
  };

  const update = () => {
    frame = 0;
    if (!mobile.matches || reduced.matches) return;
    const viewport = window.innerHeight;
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--gml-header-height')) || 80;
    const room = Math.max(240, viewport - header);
    controllers.forEach((state) => {
      if (!state.root.isConnected) { restore(state); return; }
      state.paragraphs.forEach((paragraph) => {
        const bounds = paragraph.element.getBoundingClientRect();
        const start = header + room * .85;
        const finish = header + room * .35 - Math.max(0, bounds.height - room * .45);
        const progress = clamp((start - bounds.top) / Math.max(100, start - finish));
        if (Math.abs(paragraph.progress - progress) < .0005) return;
        paragraph.progress = progress;
        const count = paragraph.words.length;
        paragraph.words.forEach((word, index) => {
          const fill = clamp(progress * (count + 2) - index);
          word.style.setProperty('--gml-word-fill', `${(fill * 100).toFixed(2)}%`);
        });
      });
      state.groups.forEach(({ sections }) => {
        sections.forEach(({ element, card }) => {
          const offset = card.element.offsetTop;
          const pinTop = Math.min(header + 16, viewport - card.element.offsetHeight - 24);
          element.style.setProperty('--gml-stack-top', `${pinTop - offset}px`);
        });
      });
      state.cards.forEach((card) => {
        const bounds = card.element.getBoundingClientRect();
        const previousRise = parseFloat(card.element.style.getPropertyValue('--gml-card-rise')) || 0;
        const naturalTop = bounds.top - previousRise;
        const progress = smooth(clamp((viewport * .97 - naturalTop) / Math.max(160, room * .42)));
        let covered = 0;
        if (card.group) {
          const group = state.groups.find((item) => item.wrapper === card.group);
          const index = group.sections.findIndex((item) => item.card === card);
          const next = group.sections[index + 1];
          if (next) {
            const nextTop = next.element.getBoundingClientRect().top;
            covered = smooth(clamp((viewport * .7 - nextTop) / Math.max(180, room * .65))) * .92;
          }
        }
        card.element.style.setProperty('--gml-card-rise', `${((1 - progress) * 30).toFixed(2)}px`);
        card.element.style.setProperty('--gml-card-opacity', ((0.4 + progress * .6) * (1 - covered)).toFixed(3));
      });
    });
  };

  function schedule() {
    if (!frame && mobile.matches && !reduced.matches) frame = requestAnimationFrame(update);
  }

  const init = () => {
    controllers.forEach((state) => { if (!state.root.isConnected || !mobile.matches) restore(state); });
    if (mobile.matches) document.querySelectorAll(rootSelector).forEach(enhance);
    schedule();
  };
  const reconfigure = () => {
    controllers.forEach(restore);
    init();
  };

  window.GMLMobileEditorial = { init };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('pageshow', init);
  window.addEventListener('gml:color-scheme', schedule);
  mobile.addEventListener('change', reconfigure);
  reduced.addEventListener('change', reconfigure);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((state) => { if (event.target.contains(state.root)) restore(state); });
  });
  document.fonts?.ready.then(schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
